import { optionalOutcome, participationOutcome } from './grading-mode.js';
import { resolveEffectiveOptions, resolveMatchedBranch, activeFeedbackForBranch } from './dropdown.js';
import { pickScenarioBranch, resolveScenarioOptions } from './scenario.js';
/**
 * The longest a free-text answer may be — the student's, and the teacher's answer key.
 *
 * Input hygiene first: this is unvalidated text going into the database, and nothing capped it.
 * The block is called Keyword because one word is wanted, so an unbounded field invites a
 * sentence that then fails a whole-answer match and leaves the student with no idea why.
 *
 * APPLIES TO BOTH SIDES, and that is not symmetry for its own sake. Matching below is
 * whole-answer, so an authored key longer than a student can type is an answer nobody can ever
 * give — unreachable, with nothing on screen saying so. One cap, both ends.
 *
 * Counted in UTF-16 code units by the platform's own `maxlength`, which differs from characters
 * only for text outside the BMP or built from combining marks. Accented Latin written normally
 * is one unit per character, so pt-BR and the other target languages count as a reader would.
 */
export const FREE_TEXT_ANSWER_MAX_CHARS = 35;
/**
 * Normalizes a keyword or student answer for whole-answer comparison:
 * always trims and collapses internal whitespace, and conditionally
 * lowercases / strips diacritics based on the block's sensitivity settings.
 */
export function normalizeKeyword(text, caseSensitive, accentSensitive) {
    let result = text.trim().replace(/\s+/g, ' ');
    if (!caseSensitive)
        result = result.toLowerCase();
    if (!accentSensitive)
        result = result.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return result;
}
/**
 * Returns the feedback states that match a student's answer.
 *
 * Evaluation sequence (mirrors evaluateDropdown, adapted for free text
 * matched against multiple possible correct keywords rather than a single
 * selected option id):
 * 1. optional mode → any_response, answered or not — no correctness check, no separate
 *    unanswered state for this mode (see positiveTriggerForMode in use-input-block-feedback.ts)
 * 2. Empty answer (graded/survey only) → unanswered state
 * 3. survey mode → any_answer (any non-empty answer counts as participation)
 * 4. graded mode → correct-first-terminal: if the matched keyword is correct,
 *    `correct` wins outright, full stop — otherwise the targeted feedback for
 *    the matched keyword fires if one exists, else `incorrect`
 *
 * Correct-first-terminal means stale feedback left over from before a keyword was
 * marked correct can never hijack the outcome. Keyword is a single free-text
 * match (at most one keyword matches per answer), so like dropdown there's no
 * "neutral floats free" nuance — a neutral-role match is unconditionally not
 * correct, same as an incorrect-role match.
 *
 * Unlike MCQ/dropdown, more than one keyword can be marked correct
 * (synonyms) — matching is "does the answer equal any one accepted term",
 * not a combinatorial set match. An answer matching no defined keyword at
 * all (the common case for free text) falls through to `incorrect`.
 *
 * Targeted trigger rule:
 *   option_X fires when the answer matches keyword X (a non-correct keyword)
 *
 * `resolvedVariables` resolves `conditionalBranches` (see ConditionalRoleBranch in dropdown.ts) —
 * optional, and a no-op whenever `blockData.conditionalBranches` is unset. When a branch resolves,
 * its OWN feedback fires instead of the block's top-level `feedback`, same reasoning as
 * evaluateDropdown/evaluateMCQ. Branches are consulted ONLY in `graded` mode, same gate and same
 * reasoning as Dropdown's own (authored exclusively there; switching away from graded doesn't
 * clear branch data, so a still-matching leftover branch must never hijack survey/optional).
 */
/**
 * WHICH AUTHORED WORD A TYPED ANSWER MATCHES, or none.
 *
 * Lifted out of `evaluateKeyword`, where it was a closure, because a second caller arrived:
 * versions driven by a keyword answer have to decide which row a typed word lands in, and
 * that is the SAME question the grader asks. Two implementations of it would be two answers
 * to "did they say oxygen", differing on case or accents, with the version shown and the
 * feedback fired disagreeing about one word.
 *
 * Match on the normalised form, never the raw text — `normalizeKeyword` is where
 * case- and accent-sensitivity actually live.
 */
export function matchedKeywordOption(typed, options, caseSensitive, accentSensitive) {
    const normalized = normalizeKeyword(typed, caseSensitive, accentSensitive);
    return options.find(o => normalizeKeyword(o.text, caseSensitive, accentSensitive) === normalized);
}
export function evaluateKeyword(answer, blockData, resolvedVariables = {}, 
/** The leader's variant in force for this attempt — see `evaluateDropdown`'s own note. */
scenarioId) {
    const { text } = answer;
    const { caseSensitive, accentSensitive, gradingMode, feedback, conditionalEquation, conditionalBranches, scenarioBranches, angleMode } = blockData;
    /* A FOLLOWER'S SCENARIO ROW REPLACES the BLOCK's numeric branch, never joins it. Checked by
       presence rather than by a flag, so two field signatures cannot disagree with themselves. */
    const followed = gradingMode === 'graded' && !!scenarioBranches && scenarioBranches.length > 0;
    const scenarioRow = followed ? pickScenarioBranch(scenarioBranches, scenarioId) : null;
    const matchedBranch = !followed && gradingMode === 'graded'
        ? resolveMatchedBranch(conditionalEquation, conditionalBranches, resolvedVariables, angleMode)
        : null;
    const options = gradingMode !== 'graded' ? blockData.options
        : followed ? resolveScenarioOptions(blockData.options, scenarioBranches, scenarioId, resolvedVariables, angleMode ?? 'degrees')
            : resolveEffectiveOptions(blockData.options, conditionalEquation, conditionalBranches, resolvedVariables, angleMode);
    // Incomplete branch coverage falls back to the top-level set with targeted states STRIPPED, so an
    // unmatched answer always lands on Default Incorrect instead of a leftover targeted state firing
    // terminally on an option that was just declared neutral. See activeFeedbackForBranch.
    const activeFeedback = activeFeedbackForBranch((scenarioRow ?? matchedBranch)?.feedback, feedback, gradingMode === 'graded'
        && (followed || (!!conditionalBranches && conditionalBranches.length > 0)));
    /**
     * Which authored keyword the answer matches, and the targeted state for it. ONE matcher, used
     * by every grading mode — see `participationOutcome`. Hoisted above the mode branches rather
     * than copied into them, so survey and optional fire exactly what graded would for the same
     * typed word.
     */
    const matchedOptionFor = (answer) => matchedKeywordOption(answer, options, caseSensitive, accentSensitive);
    const targetedFor = (answer) => {
        const option = matchedOptionFor(answer);
        return option
            ? activeFeedback.states.filter(st => st.type === 'targeted' && st.trigger === `option_${option.id}`)
            : [];
    };
    if (gradingMode === 'optional') {
        // OPTIONAL is INERT: the catch-all, whatever the student did. See `targetedFiresIn`.
        return optionalOutcome(activeFeedback.states);
    }
    if (!text.trim()) {
        const unanswered = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'unanswered');
        return unanswered ? [unanswered] : [];
    }
    if (gradingMode === 'survey') {
        return participationOutcome(activeFeedback.states, 'survey', targetedFor(text));
    }
    // Graded: find the one keyword the answer matches, if any. Correct is checked
    // first and is terminal — see doc comment above.
    const matchedOption = matchedOptionFor(text);
    if (matchedOption) {
        if (matchedOption.role === 'correct') {
            const correct = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'correct');
            return correct ? [correct] : [];
        }
        const matchedFeedback = targetedFor(text);
        if (matchedFeedback.length > 0)
            return matchedFeedback;
    }
    const incorrect = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'incorrect');
    return incorrect ? [incorrect] : [];
}
/** A branch whose role snapshot has no option marked correct — "an equation resolution that
 * won't give a correct setup," regardless of which value the variable resolves to. Unlike
 * Dropdown/MCQ's equivalent, there is no `'multiple-correct'` case: multiple keywords marked
 * correct in the same branch is a normal, intended pattern here (synonyms), not an error. */
export function keywordBranchRoleIssue(branch, options) {
    return options.some(o => branch.roles[o.id] === 'correct') ? null : 'none-correct';
}
//# sourceMappingURL=keyword.js.map